/**
 * PluginBrowsePage — Plugin marketplace browse + install surface.
 *
 * Renders a category-filtered grid of every plugin in the marketplace. Each
 * card surfaces label, vendor, category, tempo-sync badge, Pro badge, and an
 * action that either loads the plugin into the user-picked target channel
 * (defaulting to the next free channel, persisted in localStorage) or routes
 * to the PricingModal (free user clicking a Pro card).
 *
 * The data driving the grid comes from GET /api/plugins/marketplace —
 * server-side single source of truth so future community submissions can
 * appear without a frontend redeploy.
 *
 * iter-156 — fixed prior bug where every install hardcoded CH 01 by adding
 * a global "Load to channel" picker at the top of the page.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import { motion } from "framer-motion";
import {
  Search, Crown, Lock, Sparkles, Activity, Zap, AudioWaveform,
  SlidersHorizontal, Music2, ArrowRight, Plug, ArrowLeft, Plus,
} from "lucide-react";
import { toast } from "sonner";
import useAuth from "@/lib/useAuth";
import PricingModal from "@/components/PricingModal";
import { getEngine } from "@/lib/engineSingleton";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  all: Sparkles,
  dynamics: Activity,
  distortion: Zap,
  modulation: AudioWaveform,
  filter: SlidersHorizontal,
  reverb: Music2,
};

const ICON_GRADIENTS = {
  amber:    "from-amber-400 to-orange-500",
  cyan:     "from-cyan-400 to-sky-500",
  purple:   "from-purple-400 to-fuchsia-500",
  rose:     "from-rose-400 to-pink-500",
  sky:      "from-sky-400 to-blue-500",
  orange:   "from-orange-400 to-rose-500",
  fuchsia:  "from-fuchsia-500 to-purple-500",
  emerald:  "from-emerald-400 to-cyan-500",
  indigo:   "from-indigo-400 to-violet-500",
};

function PluginCard({ p, isPro, targetLabel, onLaunch, onUpgrade }) {
  const proLocked = p.pro && !isPro;
  const grad = ICON_GRADIENTS[p.iconColor] || "from-zinc-400 to-zinc-600";

  return (
    <motion.article
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      data-testid={`marketplace-card-${p.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-zinc-900/85 to-zinc-950/85 p-5 transition hover:border-white/[0.18]"
    >
      {/* Icon block */}
      <div className="relative mb-4 h-24 overflow-hidden rounded-xl">
        <div className={`absolute inset-0 bg-gradient-to-br ${grad} opacity-90`} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_60%)]" />
        <div className="absolute inset-0 grid place-items-center">
          <Plug className="h-9 w-9 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]" />
        </div>
        {p.pro && (
          <span
            data-testid={`marketplace-pro-badge-${p.id}`}
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-amber-200 ring-1 ring-amber-300/50 backdrop-blur"
          >
            <Crown className="h-2.5 w-2.5" />Pro
          </span>
        )}
        {p.tempoSync && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-200 ring-1 ring-emerald-300/40 backdrop-blur">
            <AudioWaveform className="h-2.5 w-2.5" />Sync
          </span>
        )}
      </div>

      {/* Body */}
      <h3 className="font-display text-[15px] font-semibold leading-tight text-white">{p.label}</h3>
      <p className="mt-0.5 font-mono text-[9.5px] uppercase tracking-wider text-white/45">
        {p.vendor} · {p.category} · {p.paramCount} params
      </p>
      <p className="mt-2 line-clamp-3 flex-1 font-body text-[12px] leading-snug text-white/70">
        {p.description}
      </p>

      {/* Action */}
      <div className="mt-4">
        {proLocked ? (
          <button
            type="button"
            data-testid={`marketplace-upgrade-${p.id}`}
            onClick={onUpgrade}
            className="flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-amber-500/[0.18] to-rose-500/[0.18] px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider text-amber-100 ring-1 ring-amber-400/40 transition hover:from-amber-500/[0.28] hover:to-rose-500/[0.28]"
          >
            <span className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />
              Upgrade to unlock
            </span>
            <span className="text-amber-200/90">$9.99/mo</span>
          </button>
        ) : (
          <button
            type="button"
            data-testid={`marketplace-launch-${p.id}`}
            onClick={onLaunch}
            className="flex w-full items-center justify-between rounded-xl bg-white/[0.08] px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider text-white ring-1 ring-white/15 transition hover:bg-white/[0.16] hover:ring-white/25"
          >
            <span className="flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Load to {targetLabel}
            </span>
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </motion.article>
  );
}

export default function PluginBrowsePage() {
  const { plan } = useAuth();
  const isPro =
    plan?.plan === "pro" &&
    plan?.plan_expires_at &&
    new Date(plan.plan_expires_at) > new Date();

  const [catalog, setCatalog] = useState({ plugins: [], categories: [], counts: { total: 0, free: 0, pro: 0 } });
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [pricingOpen, setPricingOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  // iter-156 — Target channel picker. "auto" = next free channel.
  // Otherwise a specific 0-based channel index. Persisted to localStorage
  // so the user's preference sticks across reloads.
  const [targetChannel, setTargetChannel] = useState(() => {
    try {
      const v = localStorage.getItem("elpm:plugin-browse:target");
      if (v === "auto" || v == null) return "auto";
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 && n < 64 ? n : "auto";
    } catch (e) { void e; return "auto"; }
  });
  useEffect(() => {
    try { localStorage.setItem("elpm:plugin-browse:target", String(targetChannel)); } catch (e) { void e; }
  }, [targetChannel]);
  const targetLabel = targetChannel === "auto"
    ? "next free CH"
    : `CH ${String(targetChannel + 1).padStart(2, "0")}`;

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/api/plugins/marketplace`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (mounted) setCatalog(d); })
      .catch(() => { /* keep empty */ });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (catalog.plugins || []).filter((p) => {
      if (activeCat !== "all" && p.category !== activeCat) return false;
      if (!q) return true;
      return (p.label + " " + p.description + " " + p.vendor).toLowerCase().includes(q);
    });
  }, [catalog.plugins, activeCat, search]);

  const handleLaunch = async (p) => {
    // Singleton-backed — even if the studio hasn't mounted yet, this lazily
    // constructs the AudioEngine + AudioContext. Once constructed, the same
    // instance is reused when the user navigates back to `/`.
    const engine = getEngine();
    if (!engine) {
      toast.error("Studio engine not available", { description: "Browser is missing Web Audio. Try a recent Chrome/Edge/Firefox." });
      return;
    }
    // iter-156 — resolve the actual channel index. "auto" picks the next
    // free channel; otherwise honour the explicit user choice. This fixes
    // the prior bug where ALL marketplace plugins landed on CH 01.
    let chIdx;
    if (targetChannel === "auto") {
      const free = engine.freeChannelIndices?.(1) || [];
      chIdx = free.length > 0 ? free[0] : 0;
    } else {
      chIdx = targetChannel;
    }
    setBusy(p.id);
    try {
      // Use the same URL convention as the registry: free is /plugins/<file>.js,
      // pro is ${API}/api/plugins/pro/<slug>.
      const url = p.pro
        ? `${API}/api/plugins/pro/${p.id}`
        : `/plugins/${p.processorName}.js`;
      const id = await engine.loadPluginToChannel(chIdx, url, {
        processorName: p.processorName,
        label: p.label,
      });
      const chLabel = `CH ${String(chIdx + 1).padStart(2, "0")}`;
      toast.success(`Plugin loaded into ${chLabel}`, {
        description: `${p.label} is now in the signal chain. Switch to the studio to tweak params.`,
        action: { label: "Open Studio", onClick: () => { window.location.href = "/"; } },
      });
      void id;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[marketplace] load failed:", e);
      toast.error("Could not load plugin", { description: e?.message || "Unknown error" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="marketplace-page"
      className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white"
    >
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-zinc-950/85 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
          <div className="mb-4 flex items-center justify-between gap-3">
            <Link
              to="/"
              data-testid="marketplace-back-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/[0.07] bg-white/[0.03] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/65 transition hover:bg-white/[0.1] hover:text-white"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to studio
            </Link>
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
              <ElpmLogo className="h-8" testId="plugins-logo" /> Plugins
            </span>
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Plugin Marketplace
              </h1>
              <p className="mt-2 max-w-xl font-body text-[13px] leading-6 text-white/55">
                Tempo-synced AudioWorklet effects, hosted by ELPM Studio. Free tier ships 6 essentials
                including the Waves-alternative starter pack (SSL Buss Comp, Pultec EQ, Vintage Tape);
                Pro tier unlocks 6 more (Sidechain Pumper, Multiband Comp, Tape Saturator, Talkbox,
                Modal Reverb, Phaser).
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                {catalog.counts?.total} plugins · {catalog.counts?.free} free · {catalog.counts?.pro} pro
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
              <input
                data-testid="marketplace-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plugins…"
                className="w-full rounded-xl border border-white/[0.07] bg-white/[0.04] py-2 pl-9 pr-3 font-mono text-[12px] text-white placeholder:text-white/30 focus:border-fuchsia-400/40 focus:outline-none"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Category tabs */}
      <div className="border-b border-white/[0.04] bg-zinc-950/65">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 overflow-x-auto px-5 py-3 sm:px-8">
          {(catalog.categories || []).map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id] || Sparkles;
            const active = activeCat === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                data-testid={`marketplace-cat-${cat.id}`}
                onClick={() => setActiveCat(cat.id)}
                className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] transition ${
                  active
                    ? "border border-fuchsia-400/40 bg-fuchsia-500/[0.18] text-fuchsia-100"
                    : "border border-white/[0.07] bg-white/[0.03] text-white/55 hover:bg-white/[0.1] hover:text-white/85"
                }`}
              >
                <Icon className="h-3 w-3" />
                {cat.label}
              </button>
            );
          })}
          {/* iter-156 — Channel target picker. Previously every install
              hardcoded CH 01 — now the user picks once and every Load
              button on the page honours it. Default "auto" finds the
              next free channel automatically. */}
          <div className="ml-auto flex flex-shrink-0 items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/[0.06] px-3 py-1.5">
            <Plug className="h-3 w-3 text-cyan-200" />
            <label htmlFor="marketplace-target-channel" className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-200/85">
              Load to
            </label>
            <select
              id="marketplace-target-channel"
              data-testid="marketplace-target-channel"
              value={String(targetChannel)}
              onChange={(e) => {
                const v = e.target.value;
                setTargetChannel(v === "auto" ? "auto" : Number(v));
              }}
              className="cursor-pointer rounded-md border border-cyan-400/20 bg-black/40 px-2 py-1 font-mono text-[10.5px] text-cyan-50 focus:border-cyan-300/60 focus:outline-none"
            >
              <option value="auto">Auto · next free CH</option>
              {Array.from({ length: 64 }, (_, i) => (
                <option key={i} value={String(i)}>{`CH ${String(i + 1).padStart(2, "0")}`}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {filtered.length === 0 ? (
          <div data-testid="marketplace-empty" className="rounded-2xl border border-dashed border-white/[0.06] bg-black/20 p-8 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">
              No plugins match "{search}" in {activeCat}.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <PluginCard
                key={p.id}
                p={p}
                isPro={isPro}
                targetLabel={targetLabel}
                onLaunch={() => handleLaunch(p)}
                onUpgrade={() => setPricingOpen(true)}
              />
            ))}
          </div>
        )}
        {busy && (
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-fuchsia-200/70">
            Loading {busy}…
          </p>
        )}
      </main>

      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
    </div>
  );
}
